import {
  Button,
  HStack,
  VStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { DialogProvider, createDialogContext } from "../../Common/Dialog";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";

export const FilterDialogContext = createDialogContext();

type FormValues = {
  preset: string;
  startDate?: Date | null;
  endDate?: Date | null;
};

const dateOptions = [
  { label: "All", value: "all" },
  { label: "Last Week", value: "7d" },
  { label: "Last Month", value: "28d" },
  { label: "Last Quarter", value: "3m" },
  { label: "Last 6 Months", value: "6m" },
  { label: "Custom", value: "custom" },
];

const FilterDialog = ({
  onSave,
  onClose,
  isLoading = false,
  initialPreset,
  initialStartDate,
  initialEndDate,
}: {
  onSave: (data: {
    startDate?: string;
    endDate?: string;
    preset: string;
  }) => void;
  onClose: () => void;
  isLoading?: boolean;
  initialPreset?: string;
  initialStartDate?: Date;
  initialEndDate?: Date;
}) => {
  const breakpoint = useBreakpointValue({ base: "column", md: "row" });
  const { isOpen } = useContext(FilterDialogContext);
  const form = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      preset: initialPreset || "all",
      startDate: initialStartDate || null,
      endDate: initialEndDate || null,
    },
  });
  const { handleSubmit, setValue, watch } = form;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const preset = watch("preset");

  const _onClose = () => {
    onClose();
  };

  const handleSave = (data: FormValues) => {
    const formattedData = {
      startDate: data.startDate
        ? dayjs(data.startDate).format("YYYY-MM-DD")
        : undefined,
      endDate: data.endDate
        ? dayjs(data.endDate).format("YYYY-MM-DD")
        : undefined,
      preset: data.preset,
    };
    onSave(formattedData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={_onClose}
      closeOnOverlayClick={false}
      size="3xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        {/* Header */}
        <ModalHeader px={6} py={4}>
          <HStack justify="space-between" w="full">
            <Text fontSize="lg" fontWeight="bold">
              Filters
            </Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={_onClose}
              rightIcon={<FiX />}
            ></Button>
          </HStack>
        </ModalHeader>

        {/* Body */}
        <ModalBody px={6} pb={6}>
          <VStack align="stretch" spacing={4}>
            <Text fontWeight="semibold">Dates</Text>

            {/* Button Group */}
            <HStack
              spacing={0}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
              w="full"
              flexWrap={breakpoint === "column" ? "wrap" : "nowrap"}
              justifyContent="center"
            >
              {dateOptions.map((opt) => (
                <Button
                  minWidth={"100px"}
                  flex="1"
                  width="fit-content"
                  key={opt.value}
                  variant={preset === opt.value ? "solid" : "outline"}
                  bg={preset === opt.value ? "black" : undefined}
                  color={preset === opt.value ? "white" : undefined}
                  _hover={{
                    bg: preset === opt.value ? "gray.800" : "gray.50",
                  }}
                  onClick={() => setValue("preset", opt.value)}
                  fontSize="sm"
                  borderRadius="none"
                  textAlign="center"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  {opt.label}
                </Button>
              ))}
            </HStack>

            {preset === "custom" && (
              <HStack
                spacing={4}
                pt={2}
                w="full"
                justifyContent="flex-start"
                gap={8}
              >
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Start Date
                  </Text>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) =>
                      setValue("startDate", date, { shouldValidate: true })
                    }
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Start Date"
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input placeholder="Start Date" />}
                  />
                </VStack>

                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    End Date
                  </Text>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) =>
                      setValue("endDate", date, { shouldValidate: true })
                    }
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    placeholderText="End Date"
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input placeholder="End Date" />}
                  />
                </VStack>
              </HStack>
            )}
          </VStack>
        </ModalBody>

        {/* Footer */}
        <ModalFooter px={6} py={4}>
          <HStack spacing={3} ml="auto">
            <Button onClick={_onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit(handleSave)}
              isLoading={isLoading}
              isDisabled={
                isLoading || (preset === "custom" && (!startDate || !endDate))
              }
            >
              Add Filter
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withFilterDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => (
    <DialogProvider context={FilterDialogContext}>
      <Component {...props} />
    </DialogProvider>
  );

export default FilterDialog;
